/*
  Intelligems bridge -- TASKS #133 price test.

  Loaded from snippets/intelligems.liquid, so it exists only while the
  Intelligems script is installed. Remove both when the test ends.

  Intelligems rewrites the price elements the theme tags for it (.igPrice /
  .igComparePrice carrying data-product-id, data-variant-id and, for a price
  times a constant, data-price-multiplier) and re-applies them to markup
  injected later. It cannot produce figures that are not a price times a
  constant, so this file works those out with Intelligems' documented
  igData.price API:

    - a stock-picker tile's subscription discount %, which feeds the
      "Save [discount]% ..." label            [data-ig-sub-discount]
    - a cart upsell's "Save Up To $X" headline [data-ig-savings-amount]
    - a bundle card's "Save up to X%"          [data-ig-max-savings-pct]

  and, for a shopper in a test group whose copies carry different plan
  discounts, the plan part of an Intelligems-drawn subscription price
  (.igPrice[data-ig-plan-pct]); see copyVariant() below.

  It also gives theme scripts the test price (Variant Picker V2 reads it):

    JiyuIntelligems.priceCents(variantId)          cents, or null outside a test
    JiyuIntelligems.compareCents(variantId)        cents, or null
    JiyuIntelligems.planPercent(variantId, pct)    Intelligems' plan %, else pct
    JiyuIntelligems.subscriptionCents(cents, pct)  rounded the way Shopify does
    JiyuIntelligems.formatMoney(cents, trimZeros)  in the shop's money format
    JiyuIntelligems.productFor(handle, variantId)  the product to load (below)
    JiyuIntelligems.subscriptionItem(item, handle) Promise, a cart item remapped
                                                   to the copy's variant + plan
    JiyuIntelligems.whenReady(ms)                  Promise, true once readable
    'jiyu:intelligems-ready' on document once prices are readable

  A shopper Intelligems returns no price for sees Liquid's values: nothing is
  rewritten, and anything rewritten earlier in the visit is put back.

  Copies. The test runs on Duplicate Products with Should Redirect on: a
  shopper in a test group is sent to Intelligems' copy of each product in the
  test (handle -1, -2, ..., tagged dup_<original product id>), because each
  group's copies carry their own Stay.ai plans. Intelligems only redirects
  full page loads, so anything that loads a product with fetch() (the family
  switcher, the homepage switcher) asks productFor() which handle and variant
  to fetch. It returns { handle, variantId, known }: the shopper's copy in a
  test group, otherwise the arguments unchanged. known is false until
  Intelligems is ready, when the answer can't be told yet.
*/
(function () {
  'use strict';

  if (window.JiyuIntelligems) return;

  var READY_EVENT = 'jiyu:intelligems-ready';
  var SELECTOR = '[data-ig-sub-discount],[data-ig-savings-amount],[data-ig-max-savings-pct],.igPrice[data-ig-plan-pct]';
  var ready = false;
  var observing = false;

  function priceApi() {
    var data = window.igData;
    return data && data.price ? data.price : null;
  }

  function call(method, id) {
    var api = priceApi();
    if (!api || id == null || id === '' || typeof api[method] !== 'function') return null;
    try {
      return api[method](String(id));
    } catch (e) {
      return null;
    }
  }

  // Intelligems returns decimal strings in the shop currency ("59.99").
  function toCents(value) {
    if (value == null || value === '') return null;
    var number = parseFloat(value);
    return isFinite(number) ? Math.round(number * 100) : null;
  }

  function intAttr(el, name) {
    var number = parseInt(el.getAttribute(name), 10);
    return isFinite(number) ? number : null;
  }

  function priceCents(variantId) {
    return toCents(call('getPriceByVariantId', variantId));
  }

  function compareCents(variantId) {
    return toCents(call('getComparePriceByVariantId', variantId));
  }

  // A copy's own subscription price, for an ORIGINAL variant shown to a
  // shopper in a test group (cart upsells, bundle cards). A group's copies can
  // carry Stay.ai plans with different discounts from the originals', and
  // Intelligems reports neither, so read the copy variant's allocation from
  // Shopify: /variants/<id>.js has its price and plan price. Returns
  // { price, subPrice, subPct } once loaded, else null; the first call starts
  // the fetch and re-applies everything when it lands. Only runs for a shopper
  // with a copy of the variant, so control and untested products never fetch.
  var copyVariants = {};
  var copyVariantsPending = {};
  var copyGeneration = 0;
  var reapplyTimer = null;

  // A preview that switches group restarts Intelligems (ig:ready again), so
  // every answer above may change; a lookup still in flight is dropped.
  function resetCopyVariants() {
    copyVariants = {};
    copyVariantsPending = {};
    copyGeneration++;
  }

  function scheduleReapply() {
    if (reapplyTimer !== null) return;
    reapplyTimer = setTimeout(function () {
      reapplyTimer = null;
      if (ready) applyWithin(document);
    }, 0);
  }

  function copyVariant(variantId) {
    if (!ready || variantId == null || variantId === '') return null;
    var key = String(variantId);
    if (Object.prototype.hasOwnProperty.call(copyVariants, key)) return copyVariants[key] || null;
    if (copyVariantsPending[key]) return null;
    var copyId = altVariantId(key);
    if (!copyId) {
      copyVariants[key] = false;
      return null;
    }
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var generation = copyGeneration;
    copyVariantsPending[key] = fetch(root + 'variants/' + encodeURIComponent(copyId) + '.js', {
      headers: { Accept: 'application/json' }
    })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then(
        function (v) {
          if (generation !== copyGeneration) return;
          var alloc = v.selling_plan_allocations && v.selling_plan_allocations[0];
          var price = Number(v.price);
          copyVariants[key] = {
            price: price,
            subPrice: alloc ? Number(alloc.price) : null,
            subPct: alloc && price > 0 ? ((price - Number(alloc.price)) * 100) / price : null
          };
          scheduleReapply();
        },
        function () {
          if (generation === copyGeneration) copyVariants[key] = false;
        }
      )
      .then(function () {
        if (generation === copyGeneration) delete copyVariantsPending[key];
      });
    return null;
  }

  // The plan's percentage for this shopper: a test-group copy's own plan when
  // known (above), then a discount Intelligems reports as tested, then the
  // plan's own percentage from Liquid.
  function planPercent(variantId, fallback) {
    var copy = copyVariant(variantId);
    if (copy && copy.subPct != null) return copy.subPct;
    var discount = call('getSubscriptionDiscountByVariantId', variantId);
    if (discount && discount.subscriptionDiscountType === 'percentage') {
      var tested = parseFloat(discount.subscriptionDiscount);
      if (isFinite(tested) && tested > 0) return tested;
    }
    var plan = parseFloat(fallback);
    return isFinite(plan) ? plan : null;
  }

  // The names of the shopper's own groups in the experiments they're in,
  // control groups left out.
  function shopperGroupNames() {
    var user = window.igData && window.igData.user;
    if (!user || typeof user.getExperiments !== 'function' || typeof user.getTestGroup !== 'function') return [];
    var names = [];
    try {
      (user.getExperiments() || []).forEach(function (experiment) {
        var group = experiment && user.getTestGroup(experiment.id);
        if (group && group.name && !group.isControl) names.push(group.name);
      });
    } catch (e) {}
    return names;
  }

  // Intelligems keys each copy by the NAME of the group it belongs to
  // ({ "New Group 1": "<handle>-2" }) and, asked without a group, returns the
  // only copy there is to anyone, control included (getAltProductHandleSync,
  // read 2026-10-05). Until then the switchers sent control shoppers to
  // Group 1's copies (TASKS #198). So ask for the shopper's own groups. It
  // returns null outside a test and for products not in it; only a different,
  // non-empty value counts.
  function altHandle(handle) {
    var api = priceApi();
    if (!api || !handle || typeof api.getAltProductHandle !== 'function') return null;
    var names = shopperGroupNames();
    for (var i = 0; i < names.length; i++) {
      var alt = null;
      try {
        alt = api.getAltProductHandle(String(handle), names[i]);
      } catch (e) {}
      if (typeof alt === 'string' && alt !== '' && alt !== String(handle)) return alt;
    }
    return null;
  }

  function altVariantId(variantId) {
    var alt = call('getAltVariantId', variantId);
    return alt != null && alt !== '' && String(alt) !== String(variantId) ? String(alt) : null;
  }

  // See "Copies" in the header. A copy found without a matching variant drops
  // the variant rather than send the original's id to the copy's page, which
  // Shopify would ignore anyway.
  function productFor(handle, variantId) {
    var original = { handle: handle, variantId: variantId || null, known: ready };
    if (!ready) return original;
    var copy = altHandle(handle);
    if (!copy) return original;
    return { handle: copy, variantId: variantId ? altVariantId(variantId) : null, known: true };
  }

  // A cart add of an ORIGINAL product with a subscription plan (the cart
  // drawer's upsells). Intelligems swaps in the copy's variant at add to cart
  // but keeps the plan id, which the copy doesn't carry, so Shopify rejects
  // the add ("Cannot apply selling plan to variant", seen live 2026-09-23).
  // For a shopper with a copy, send the copy's variant with the copy
  // variant's own plan. Every JIYU variant carries one plan (the 1-jar copy
  // three identical ones), so its first allocation is the equivalent. Returns
  // a Promise of the item; anything it can't map goes out unchanged.
  var copyProducts = {};
  function copyProduct(handle) {
    if (!copyProducts[handle]) {
      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      copyProducts[handle] = fetch(root + 'products/' + encodeURIComponent(handle) + '.js', {
        headers: { Accept: 'application/json' }
      }).then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      });
      copyProducts[handle].catch(function () {
        delete copyProducts[handle];
      });
    }
    return copyProducts[handle];
  }

  function subscriptionItem(item, productHandle) {
    if (!ready || !item || !item.selling_plan || !productHandle) return Promise.resolve(item);
    var copyVariant = altVariantId(item.id);
    var copyHandle = altHandle(productHandle);
    if (!copyVariant || !copyHandle) return Promise.resolve(item);
    return copyProduct(copyHandle).then(
      function (product) {
        var variant = (product.variants || []).filter(function (v) {
          return String(v.id) === copyVariant;
        })[0];
        var alloc = variant && variant.selling_plan_allocations && variant.selling_plan_allocations[0];
        if (!alloc) return item;
        var out = {};
        for (var key in item) {
          if (Object.prototype.hasOwnProperty.call(item, key)) out[key] = item[key];
        }
        out.id = Number(copyVariant);
        out.selling_plan = Number(alloc.selling_plan_id);
        return out;
      },
      function () {
        return item;
      }
    );
  }

  // Resolves true once prices are readable, or with the current state after
  // timeoutMs. The first wait that runs out ends the waiting: a visit where
  // Intelligems never loads (a blocker, a network error) pays the delay once,
  // not on every switch. A late load still flips ready and fires the event.
  var waitedOut = false;
  function whenReady(timeoutMs) {
    if (ready || waitedOut) return Promise.resolve(ready);
    return new Promise(function (resolve) {
      var settled = false;
      function finish() {
        if (settled) return;
        settled = true;
        document.removeEventListener(READY_EVENT, finish);
        if (!ready) waitedOut = true;
        resolve(ready);
      }
      document.addEventListener(READY_EVENT, finish);
      setTimeout(finish, timeoutMs > 0 ? timeoutMs : 0);
    });
  }

  // Shopify rounds the discount, not the discounted price: 5999 at 15% is
  // 5999 - round(899.85) = 5099. Matching it keeps the page on the cent that
  // checkout charges.
  function subscriptionCents(cents, pct) {
    if (cents == null || pct == null) return cents;
    return cents - Math.round((cents * pct) / 100);
  }

  // The shop's money format, passed in by snippets/intelligems.liquid.
  function formatMoney(cents, trimZeros) {
    var format = typeof window.jiyuMoneyFormat === 'string' ? window.jiyuMoneyFormat : '${{amount}}';
    var token = format.match(/\{\{\s*(\w+)\s*\}\}/);
    var style = token ? token[1] : 'amount';
    var value = Math.round(Number(cents) || 0);

    function delimit(precision, thousands, decimal) {
      var parts = (Math.abs(value) / 100).toFixed(precision).split('.');
      var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      var fraction = parts[1] && !(trimZeros && /^0+$/.test(parts[1])) ? decimal + parts[1] : '';
      return (value < 0 ? '-' : '') + whole + fraction;
    }

    var amount;
    switch (style) {
      case 'amount_no_decimals':
        amount = delimit(0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        amount = delimit(2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        amount = delimit(0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        amount = delimit(2, "'", '.');
        break;
      default:
        amount = delimit(2, ',', '.');
    }
    return token ? format.replace(token[0], amount) : amount;
  }

  // The value Liquid rendered, kept the first time the bridge sees an element.
  // A shopper with no test price gets it back, e.g. after a preview switches
  // group without a reload.
  function liquidValue(el, read) {
    if (el.__igLiquidValue === undefined) el.__igLiquidValue = read();
    return el.__igLiquidValue;
  }

  // Stock picker tile. Liquid worked the discount out against the catalog
  // price: (compare-at when set, else price) against the plan price.
  function applySubDiscount(el) {
    var next = liquidValue(el, function () {
      return el.getAttribute('data-subscription-discount');
    });
    var id = el.getAttribute('data-ig-variant');
    var price = priceCents(id);
    if (price != null) {
      var compare = compareCents(id);
      if (compare == null) compare = intAttr(el, 'data-ig-compare-cents');
      var base = compare != null ? compare : price;
      var pct = planPercent(id, el.getAttribute('data-ig-plan-pct'));
      if (pct != null && base > 0) {
        var plan = subscriptionCents(price, pct);
        next = String(plan < base ? Math.round(((base - plan) * 100) / base) : 0);
      }
    }
    if (next == null || el.getAttribute('data-subscription-discount') === next) return false;
    el.setAttribute('data-subscription-discount', next);
    return true;
  }

  // Cart upsell headline: a fixed compare-at total minus what the shopper would
  // pay for the upsold variant.
  function applySavingsAmount(el) {
    var text = liquidValue(el, function () {
      return el.textContent;
    });
    var id = el.getAttribute('data-ig-variant');
    var price = priceCents(id);
    if (price != null) {
      var total = intAttr(el, 'data-ig-compare-total');
      if (el.getAttribute('data-ig-compare-from-variant') === 'true') {
        var compare = compareCents(id);
        if (compare != null) total = compare;
      }
      if (total != null) {
        var planPct = el.getAttribute('data-ig-plan-pct');
        var paid = planPct ? subscriptionCents(price, planPercent(id, planPct)) : price;
        if (total - paid > 0) text = formatMoney(total - paid, false);
      }
    }
    if (el.textContent !== text) el.textContent = text;
  }

  // Bundle card: the best saving across every pack, one-time and subscription,
  // rounded to the nearest point, exactly as snippets/bundle-grid-card.liquid
  // works it out.
  function applyMaxSavingsPct(el) {
    var liquid = liquidValue(el, function () {
      return { text: el.textContent, pct: el.getAttribute('data-ig-pct') };
    });
    var packs;
    try {
      packs = JSON.parse(el.getAttribute('data-ig-savings') || '[]');
    } catch (e) {
      return;
    }
    var sale = parseFloat(el.getAttribute('data-ig-sale-pct')) || 0;
    var best = 0;
    var tested = false;
    packs.forEach(function (pack) {
      var price = priceCents(pack.id);
      if (price != null) {
        tested = true;
      } else {
        price = pack.p;
      }
      var compare = compareCents(pack.id);
      if (compare == null) compare = pack.c;
      if (!(compare > 0)) return;
      var amounts = [price];
      (pack.s || []).forEach(function (pct) {
        amounts.push(subscriptionCents(price, planPercent(pack.id, pct)));
      });
      amounts.forEach(function (amount) {
        if (sale > 0 && amount > 0) amount -= Math.floor((amount * sale) / 100);
        if (compare > amount) best = Math.max(best, Math.round(((compare - amount) * 100) / compare));
      });
    });
    // Always rebuilt from Liquid's text, so one test value never feeds the next.
    var text = liquid.text;
    var shown = liquid.pct;
    var at = shown ? text.indexOf(shown) : -1;
    if (tested && best > 0 && at >= 0) {
      text = text.slice(0, at) + best + text.slice(at + shown.length);
      shown = String(best);
    }
    if (el.textContent !== text) el.textContent = text;
    if (shown != null && el.getAttribute('data-ig-pct') !== shown) el.setAttribute('data-ig-pct', shown);
  }

  // An Intelligems-rendered price with a plan discount in its multiplier
  // (.igPrice carrying data-ig-plan-pct, from snippets/ig-price-attrs.liquid)
  // for an ORIGINAL variant. Intelligems draws it as the group price times the
  // multiplier, whose plan part is the original's. For a shopper with a copy,
  // swap in the copy's plan and write the figure, so that when Intelligems
  // draws it again it reads the same multiplier and lands on the same number.
  // Anyone else gets Liquid's multiplier back and Intelligems' own figure.
  function applyPlanMultiplier(el) {
    var liquidMult = liquidValue(el, function () {
      return el.getAttribute('data-price-multiplier') || '1';
    });
    var id = el.getAttribute('data-variant-id');
    var origPct = parseFloat(el.getAttribute('data-ig-plan-pct'));
    var copy = copyVariant(id);
    var mult = liquidMult;
    if (copy && copy.subPct != null && isFinite(origPct) && origPct < 100) {
      mult = String(Math.round(((parseFloat(liquidMult) * (100 - copy.subPct)) / (100 - origPct)) * 1e6) / 1e6);
    }
    if (el.getAttribute('data-price-multiplier') !== mult) el.setAttribute('data-price-multiplier', mult);
    if (!copy || copy.subPct == null) return;
    var price = priceCents(id);
    if (price == null) return;
    var text = formatMoney(Math.round(price * parseFloat(mult)));
    if (el.textContent !== text) el.textContent = text;
  }

  function applyWithin(scope) {
    var nodes = [];
    if (scope.nodeType === 1 && scope.matches(SELECTOR)) nodes.push(scope);
    Array.prototype.push.apply(nodes, scope.querySelectorAll(SELECTOR));
    var labelChanged = false;
    nodes.forEach(function (el) {
      if (el.hasAttribute('data-ig-sub-discount')) {
        if (applySubDiscount(el)) labelChanged = true;
      } else if (el.hasAttribute('data-ig-savings-amount')) {
        applySavingsAmount(el);
      } else if (el.hasAttribute('data-ig-plan-pct')) {
        applyPlanMultiplier(el);
      } else {
        applyMaxSavingsPct(el);
      }
    });
    // global.js fills that label from the checked tile's data-subscription-discount.
    if (labelChanged && typeof window.updateSubscriptionDiscountText === 'function') {
      window.updateSubscriptionDiscountText();
    }
  }

  var queued = [];
  var flushTimer = null;

  function flush() {
    flushTimer = null;
    var batch = queued;
    queued = [];
    batch.forEach(function (node) {
      if (node.isConnected) applyWithin(node);
    });
  }

  // Product family tabs, the homepage switcher and cart drawer re-renders all
  // insert fresh catalog-priced markup. Watches element insertions only: every
  // write in this file replaces a text node or sets an attribute, so none of
  // them can re-trigger the observer. setTimeout, not rAF, so it still runs in a
  // background tab.
  function observe() {
    if (observing || !window.MutationObserver || !document.body) return;
    observing = true;
    new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          if (node.matches(SELECTOR) || node.querySelector(SELECTOR)) queued.push(node);
        }
      }
      if (queued.length && flushTimer === null) flushTimer = setTimeout(flush, 0);
    }).observe(document.body, { childList: true, subtree: true });
  }

  function onReady() {
    if (!priceApi()) return;
    ready = true;
    applyWithin(document);
    observe();
    document.dispatchEvent(new CustomEvent(READY_EVENT));
  }

  // Intelligems marks performance with 'ig:ready' just before it dispatches the
  // event, so this deferred file can tell when the event already went by.
  function intelligemsWasReady() {
    try {
      return performance.getEntriesByName('ig:ready').length > 0;
    } catch (e) {
      return false;
    }
  }

  function whenDomReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  }

  window.JiyuIntelligems = {
    READY_EVENT: READY_EVENT,
    priceCents: priceCents,
    compareCents: compareCents,
    planPercent: planPercent,
    subscriptionCents: subscriptionCents,
    formatMoney: formatMoney,
    productFor: productFor,
    subscriptionItem: subscriptionItem,
    whenReady: whenReady,
    isReady: function () {
      return ready;
    }
  };

  // Every ig:ready re-applies: Intelligems restarts itself when a preview
  // switches test groups.
  window.addEventListener('ig:ready', function () {
    resetCopyVariants();
    whenDomReady(onReady);
  });
  whenDomReady(function () {
    if (!ready && intelligemsWasReady()) onReady();
  });
})();
